import {
  ClientExperiments,
  ClientReviewRequestResult,
  ConversionResponse,
  DocumentID,
  FetchBoundaries,
  ProductResponse,
  ProductVariantUID,
  ProductWidget,
  RawProductsResponse,
  RecordInteractionRequest,
  SizeRecommendationRequestResult,
  SupportedMatchInfoHashCategory,
  TranslateReviewRequest,
  TranslateReviewResponse,
  UserLikePostStatusRequest,
  UserModel,
  VisitEventType,
  WidgetStylesConfiguration,
} from '@vestico/model';

interface InfraConfigurations {
  element?: HTMLElement;
}

export interface ProductConfiguration {
  productUID: string;
  isTag?: boolean;
  variant?: string;
  size?: string; // @deprecated
}

export interface ProductCollectionConfiguration {
  productCollectionUID: string | undefined;
}

export interface AuthInfo {
  apiKey: string;
  token: string;
  expiryDate: number; // UNIX ms
}

export interface BaseConfigurations {
  clientId: string;
  iOSBundleIdentifier?: string;
  sdkAPIKey?: string;
  experiments?: ClientExperiments;
  reportingDisabled?: boolean;
}

export interface ConversionConfigurations extends BaseConfigurations {
  orderUID: string;
  items: ProductConfiguration[];
}

/**
 * A model representing all possible configurations
 * that can be done from embedded script. Those settings
 * are passed around in application via Context.
 */
export interface AppConfigurations extends BaseConfigurations {
  buttonClass?: string;
  disableDarkMode: boolean;
  styles?: WidgetStylesConfiguration;
  language?: string;
  /** @deprecated Use products array instead */
  product?: ProductConfiguration;
  products?: ProductConfiguration[];
  productCollection?: ProductCollectionConfiguration;
  isPreview: boolean;
  isFineTune?: boolean;
  isShowcase?: boolean;
  isQuiz?: boolean;
  isSDK?: boolean; // set internally
  currencyCode?: string;
  type?: 'shopify';
}

export const ProductResponseRetry = 'retry';

export type Configurations = InfraConfigurations & AppConfigurations;

export interface AuthorizedRequestInfo {
  authInfo: AuthInfo | null;
  resetAuth: () => Promise<AuthInfo | null>;
}

export interface AuthApi {
  fetchAuthInfo: (isRefetch?: boolean) => Promise<AuthInfo | null>;
}
export interface ProductApi {
  fetchProduct: (
    productConfig: ProductConfiguration | ProductConfiguration[],
    userUID: string,
    authInfo: AuthorizedRequestInfo,
    isRetry: boolean
  ) => Promise<ProductResponse | null | typeof ProductResponseRetry>;
  fetchRawProducts: (
    productDocumentIDs: DocumentID[],
    providerDocumentID: DocumentID,
    authInfo: AuthorizedRequestInfo
  ) => Promise<RawProductsResponse>;
  fetchWidgetStyles: (authInfo: AuthorizedRequestInfo) => Promise<WidgetStylesConfiguration | null>;
}

export interface MatchAPIRequest {
  product: ProductWidget;
  selectedVariantUID: ProductVariantUID | undefined;
  user: UserModel;
  authRequestInfo: AuthorizedRequestInfo;
}

export interface ContentAPIRequest {
  providerDocumentID: DocumentID;
  entryID: DocumentID;
  user: UserModel;
  authRequestInfo: AuthorizedRequestInfo;
  category: SupportedMatchInfoHashCategory;
  selectedPost: DocumentID | undefined;
  reload: boolean;
  referenceFetchBoundaries: FetchBoundaries | undefined;
  // If on PDP
  product: ProductWidget | undefined;
  selectedVariantUID: ProductVariantUID | undefined;
  // If on PLPL
  productCollection: ProductCollectionConfiguration | undefined;
}

export interface MatchingApi {
  fetchMatches: (request: MatchAPIRequest) => Promise<ClientReviewRequestResult | null>;
  fetchContentResponses: (request: ContentAPIRequest) => Promise<ClientReviewRequestResult | null>;
  fetchSizeRecommendations: (request: MatchAPIRequest) => Promise<SizeRecommendationRequestResult | null>;
  translateReviewText: (request: TranslateReviewRequest, authInfo: AuthorizedRequestInfo) => Promise<TranslateReviewResponse | null>;
  recordSizeRecFineTune: (request: MatchAPIRequest) => Promise<[success: boolean, error: Error | undefined]>;
}
export interface ConversionApi {
  recordConversion: (
    orderUID: string,
    items: ProductConfiguration[],
    user: UserModel,
    authInfo: AuthorizedRequestInfo
  ) => Promise<ConversionResponse | null>;
}
export interface VisitsApi {
  recordLoadEvent: (
    items: ProductConfiguration | ProductConfiguration[] | undefined,
    user: string | undefined,
    isShowcase: boolean | undefined,
    authInfo: AuthorizedRequestInfo
  ) => Promise<string | undefined>;
  recordImpression: (widgetEntryID: string, user: string | undefined, authInfo: AuthorizedRequestInfo) => Promise<string | undefined>;
  recordVisitEvent: (
    type: VisitEventType,
    items: ProductWidget[] | undefined,
    user: string,
    authInfo: AuthorizedRequestInfo
  ) => Promise<string | undefined>;
}
export interface InteractionsApi {
  recordInteraction: (request: RecordInteractionRequest, authInfo: AuthorizedRequestInfo) => Promise<boolean>;
  getUserLikedPost: (request: UserLikePostStatusRequest, authInfo: AuthorizedRequestInfo) => Promise<boolean>;
}

export const getProductConfigUIDs = (productConfig: ProductConfiguration | ProductConfiguration[] | undefined): string[] => {
  if (!productConfig) {
    return [];
  }
  if (Array.isArray(productConfig)) {
    const products = productConfig as ProductConfiguration[];
    return products.map((p) => p.productUID);
  }
  return [productConfig.productUID];
};

export const getProductConfigUIDsFromAppConfig = (appConfig: AppConfigurations): string[] => {
  // Prioritize the new products array over the legacy product field
  if (appConfig.products && appConfig.products.length > 0) {
    return appConfig.products.map((p) => p.productUID);
  }

  // Fallback to legacy product field
  if (appConfig.product) {
    return [appConfig.product.productUID];
  }

  return [];
};

export const authInfoIsValid = (authInfo: AuthInfo | null | undefined, clientID?: string): boolean => {
  if (!authInfo) {
    return false;
  }

  const { expiryDate } = authInfo;

  if (!expiryDate || expiryDate < Date.now()) {
    console.info("Token expired. Can't fetch APIs");
    console.debug(clientID);
    // logAnalyticsEvent({
    //   category: 'exception',
    //   event: 'token_exp_when_fetch',
    //   dimensions: { provider: clientID ?? undefined },
    // });
    return false;
  }

  return true;
};
